import type { IconName } from '@launchpad-ui/icons';

import type { ModelProvider } from '#components/AiConfigs/AiConfigModelConfiguration/modelSchemas/aiConfigModelService';

import type { AiConfigMode, AiConfigModeTabId } from './types';

export const chartBucketTypes = [
  'Tokens',
  'Satisfaction',
  'Generations',
  'Time to generate',
  'Error rate',
  'Time to first token',
  'Costs',
  'Evaluator metrics',
] as const;

export type ChartBucketType = (typeof chartBucketTypes)[number];

export const chartTypes = [
  // Tokens
  'Tokens out (avg)',
  'Tokens in (avg)',
  'Tokens (avg)',
  // Satisfaction
  'Satisfaction rating',
  'Thumbs down on average',
  'Thumbs up on average',
  // Generations
  'Generations on average',
  // Latency
  'Time to generate',
  // Error rate
  'Error rate',
  'Time to 1st token (avg)',
  // Costs
  'Output cost',
  'Input cost',
  // Judge Metrics
  'Judge accuracy',
  'Judge relevance',
  'Judge toxicity',
] as const;

export const MonitoringDashboardChartTypeEnum = {
  TokensOut: 'Tokens out (avg)',
  TokensIn: 'Tokens in (avg)',
  Tokens: 'Tokens (avg)',
  Satisfaction: 'Satisfaction rating',
  ThumbsDown: 'Thumbs down on average',
  ThumbsUp: 'Thumbs up on average',
  Generations: 'Generations on average',
  Latency: 'Time to generate',
  ErrorRate: 'Error rate',
  TimeToFirstToken: 'Time to 1st token (avg)',
  OutputCost: 'Output cost',
  InputCost: 'Input cost',
  JudgeAccuracy: 'Judge accuracy',
  JudgeRelevance: 'Judge relevance',
  JudgeToxicity: 'Judge toxicity',
} as const;

export const iconNameByMode: Record<AiConfigMode, IconName> = {
  agent: 'robot',
  completion: 'chat-bubble-outline',
  judge: 'scale',
};

/**
 * Shared mode option definitions for AI Configs.
 * Used by both tabs and filter dialogs to ensure consistent mode handling.
 */
export type AiConfigModeOption = {
  id: AiConfigModeTabId;
  label: string;
};

export const AI_CONFIG_MODES: readonly AiConfigModeOption[] = [
  { id: 'all', label: 'All' },
  { id: 'completion', label: 'Completion' },
  { id: 'agent', label: 'Agent' },
  { id: 'judge', label: 'Judge' },
] as const;

export const RESTRICTED_AI_CONFIG_KEY = 'new';

// Online Evaluation (Judges) constants
export const EDITABLE_EVAL_KEY_PREFIX = '$ld:ai:judge:';
export const EVENT_KEY_TOOLTIP = 'Identifies the metric used to record and display evaluation scores';
export const DESIRED_DIRECTION_TOOLTIP = 'Defines whether higher or lower scores indicate better results';
export const SAMPLING_RATE_TOOLTIP = 'Controls the percentage of responses evaluated';

type SuggestedModel = { name: string; description: string };

export const suggestedModels: Partial<Record<ModelProvider, SuggestedModel>> = {
  OpenAI: {
    name: 'gpt-5',
    description:
      "OpenAI's latest flagship model with state-of-the-art reasoning and tool-calling performance, ideal for complex multi-step agent workflows and long-horizon planning",
  },
  Anthropic: {
    name: 'claude-sonnet-4-5',
    description:
      "Anthropic's most advanced model, leading in agentic coding tasks with exceptional reasoning capabilities for sophisticated problem-solving and code generation",
  },
  Gemini: {
    name: 'gemini-1.5-pro-002',
    description:
      "Google's powerful reasoning model with strong performance on complex agent tasks, excellent for multi-step workflows requiring high accuracy and multimodal understanding",
  },
  Bedrock: {
    name: 'amazon.nova-2-pro-v1:0',
    description:
      "Amazon's most intelligent reasoning model designed specifically for agentic workflows, excelling at agentic coding, long-range planning, and sophisticated problem-solving",
  },
};

// more strictly typed signature always returns a suggested model
export function getSuggestedModel(
  provider: Extract<ModelProvider, 'OpenAI' | 'Anthropic' | 'Gemini' | 'Bedrock'>,
): SuggestedModel;
export function getSuggestedModel(provider: ModelProvider): SuggestedModel | undefined {
  return suggestedModels[provider];
}
